import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import type {
  ClientDetailRow,
  InvoiceListRow,
  MatterRow,
} from "@themislex/shared";
import { api } from "../../api/client";
import { useApi } from "../../api/hooks";
import { ClientFormModal } from "../../components/ClientFormModal";
import { InvoiceTable } from "../../components/InvoiceTable";
import {
  Badge,
  Button,
  Card,
  CopyField,
  EmptyState,
  ErrorNote,
  Money,
  PageHeader,
  Spinner,
} from "../../components/ui";

export default function ClientDetail() {
  const { id = "" } = useParams();
  const [editOpen, setEditOpen] = useState(false);
  const [setupUrl, setSetupUrl] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);

  const client = useApi<{ client: ClientDetailRow }>(`/api/clients/${id}`);
  const matters = useApi<{ matters: MatterRow[] }>(`/api/matters?clientId=${id}`);
  const invoices = useApi<{ invoices: InvoiceListRow[] }>(
    `/api/invoices?clientId=${id}`,
  );

  const resetSetup = async () => {
    setResetting(true);
    try {
      const body = await api<{ setupUrl: string }>(
        `/api/clients/${id}/reset-setup`,
        { method: "POST" },
      );
      setSetupUrl(body.setupUrl);
      await client.refetch();
    } catch {
      setSetupUrl(null);
    } finally {
      setResetting(false);
    }
  };

  if (client.loading) {
    return (
      <div className="flex justify-center py-16 text-muted">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }
  if (client.error || !client.data) {
    return <ErrorNote>{client.error ?? "Client not found."}</ErrorNote>;
  }

  const c = client.data.client;
  const clientInvoices = invoices.data?.invoices ?? [];

  return (
    <>
      <PageHeader
        title={c.name}
        subtitle={`Client since ${c.createdAt.slice(0, 10)}`}
        actions={
          <>
            <Link to={`/desk/matters/new?clientId=${c.id}`} className="btn btn-secondary no-underline">
              New matter
            </Link>
            <Link
              to={`/desk/invoices/new?clientId=${c.id}`}
              className="btn btn-secondary no-underline"
            >
              New invoice
            </Link>
            <Button variant="primary" onClick={() => setEditOpen(true)}>
              Edit profile
            </Button>
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <Card title="Profile" className="lg:col-span-2">
          <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
            {[
              { label: "Email", value: c.email },
              { label: "Phone", value: c.phone ?? "—" },
              { label: "Address", value: c.address ?? "—" },
              {
                label: "City / State / PIN",
                value:
                  [c.city, c.state, c.pincode].filter(Boolean).join(", ") || "—",
              },
            ].map((row) => (
              <div key={row.label}>
                <dt className="text-xs font-semibold uppercase tracking-wide text-muted">
                  {row.label}
                </dt>
                <dd className="mt-0.5 text-sm">{row.value}</dd>
              </div>
            ))}
          </dl>
          {c.notes && (
            <div className="mt-4 border-t border-border pt-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted">
                Intake notes
              </p>
              <p className="mt-1 whitespace-pre-line text-sm text-muted">{c.notes}</p>
            </div>
          )}
        </Card>

        <div className="space-y-6">
          <Card title="Account standing">
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-muted">Pending balance</span>
              <span className="font-heading text-xl font-bold">
                {c.balanceMinor > 0 ? <Money minor={c.balanceMinor} /> : "Nil"}
              </span>
            </div>
            <div className="mt-3 flex items-baseline justify-between">
              <span className="text-sm text-muted">Matters</span>
              <span className="font-heading text-xl font-bold">{c.matterCount}</span>
            </div>
          </Card>

          <Card title="Client login">
            <div className="flex items-center justify-between gap-3">
              <Badge variant={c.setupPending ? "warning" : "success"}>
                {c.setupPending ? "Setup pending" : "Password set"}
              </Badge>
              <Button
                variant="secondary"
                loading={resetting}
                onClick={() => void resetSetup()}
              >
                {c.setupPending ? "Show new setup link" : "Reset setup link"}
              </Button>
            </div>
            {setupUrl && (
              <div className="mt-4 space-y-3">
                <p className="text-xs text-muted">
                  One-time link, shown only now. The client sets their own password
                  with it; it expires in 14 days.
                </p>
                <CopyField value={setupUrl} />
              </div>
            )}
          </Card>
        </div>
      </div>

      <div className="mt-8 space-y-8">
        <Card
          title="Matters"
          actions={
            <Link to={`/desk/matters?clientId=${c.id}`} className="btn btn-ghost px-2 py-1 text-xs">
              View in matters list
            </Link>
          }
        >
          {matters.loading ? (
            <Spinner />
          ) : matters.data && matters.data.matters.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Title</th>
                    <th scope="col">Case type</th>
                    <th scope="col">Case number</th>
                    <th scope="col">Next hearing</th>
                  </tr>
                </thead>
                <tbody>
                  {matters.data.matters.map((m) => (
                    <tr key={m.id}>
                      <td className="font-medium">
                        <Link to={`/desk/matters/${m.id}`} className="text-accent-text hover:underline">
                          {m.title}
                        </Link>
                      </td>
                      <td>{m.caseType}</td>
                      <td>{m.caseNumber ?? "—"}</td>
                      <td className="whitespace-nowrap">
                        {m.nextHearingDate ?? "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState
              title="No matters yet."
              hint="Create the first matter for this client."
              action={
                <Link to={`/desk/matters/new?clientId=${c.id}`} className="btn btn-primary no-underline">
                  New matter
                </Link>
              }
            />
          )}
        </Card>

        <Card title="Invoices">
          <InvoiceTable invoices={clientInvoices} emptyHint="No invoices raised for this client yet." />
        </Card>
      </div>

      <ClientFormModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        initial={c}
        onSaved={() => void client.refetch()}
      />
    </>
  );
}
